import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  keyframes,
} from '@mui/material';
import {
  Pets,
  Agriculture,
  Grass,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../design-system/tokens/colors';

interface AgricultureLoadingProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'animal' | 'farm' | 'general';
}

// Animações personalizadas mais sofisticadas
const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0) scale(1);
  }
  40% {
    transform: translateY(-12px) scale(1.05);
  }
  60% {
    transform: translateY(-6px) scale(1.02);
  }
`;

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(0, 78, 100, 0.4);
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
    box-shadow: 0 0 0 10px rgba(0, 78, 100, 0.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(0, 78, 100, 0);
  }
`;

const rotateAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const grassAnimation = keyframes`
  0%, 100% {
    transform: rotate(-3deg) scale(1);
  }
  50% {
    transform: rotate(3deg) scale(1.05);
  }
`;

const shimmerAnimation = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

export const AgricultureLoading: React.FC<AgricultureLoadingProps> = ({
  message = 'Carregando...',
  size = 'medium',
  variant = 'general',
}) => {
  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return {
          containerSize: 120,
          iconSize: 32,
          progressSize: 60,
          fontSize: '0.875rem',
        };
      case 'large':
        return {
          containerSize: 200,
          iconSize: 56,
          progressSize: 100,
          fontSize: '1.25rem',
        };
      default: // medium
        return {
          containerSize: 160,
          iconSize: 40,
          progressSize: 80,
          fontSize: '1rem',
        };
    }
  };

  const getVariantConfig = () => {
    switch (variant) {
      case 'animal':
        return {
          icon: Pets,
          color: DESIGN_TOKENS.colors.organization.farm,
          message: message || 'Processando informações do animal...',
          animation: bounceAnimation,
        };
      case 'farm':
        return {
          icon: Agriculture,
          color: DESIGN_TOKENS.colors.primary[600],
          message: message || 'Carregando dados da fazenda...',
          animation: pulseAnimation,
        };
      default: // general
        return {
          icon: Grass,
          color: DESIGN_TOKENS.colors.organization.farm,
          message: message || 'Carregando...',
          animation: grassAnimation,
        };
    }
  };

  const sizeConfig = getSizeConfig();
  const variantConfig = getVariantConfig();
  const IconComponent = variantConfig.icon;

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: sizeConfig.containerSize,
          padding: 4,
          textAlign: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at center, ${variantConfig.color}08 0%, transparent 70%)`,
            borderRadius: 3,
            animation: `${pulseAnimation} 3s ease-in-out infinite`,
          },
        }}
      >
        {/* Container principal com efeito de flutuação */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            animation: `${floatAnimation} 3s ease-in-out infinite`,
            zIndex: 1,
          }}
        >
          {/* Container com ícone e progress circular */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            {/* Progress circular externo */}
            <CircularProgress
              size={sizeConfig.progressSize + 20}
              thickness={2}
              sx={{
                color: `${variantConfig.color}30`,
                position: 'absolute',
                animation: `${rotateAnimation} 4s linear infinite`,
              }}
            />
            
            {/* Progress circular principal */}
            <CircularProgress
              size={sizeConfig.progressSize}
              thickness={3}
              sx={{
                color: variantConfig.color,
                animation: `${rotateAnimation} 2s linear infinite`,
                filter: 'drop-shadow(0 0 8px rgba(0, 78, 100, 0.3))',
              }}
            />
            
            {/* Ícone central com efeitos avançados */}
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: sizeConfig.iconSize + 8,
                height: sizeConfig.iconSize + 8,
                borderRadius: '50%',
                background: `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)`,
                boxShadow: `
                  0 4px 20px rgba(0, 0, 0, 0.1),
                  0 0 0 1px rgba(255, 255, 255, 0.2),
                  inset 0 1px 0 rgba(255, 255, 255, 0.6)
                `,
                animation: `${variantConfig.animation} 2s ease-in-out infinite`,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  right: -2,
                  bottom: -2,
                  borderRadius: '50%',
                  background: `conic-gradient(from 0deg, rgba(0, 78, 100, 0.25), transparent, rgba(0, 78, 100, 0.25))`,
                  animation: `${rotateAnimation} 3s linear infinite`,
                  zIndex: -1,
                },
              }}
            >
              {variant === 'animal' ? (
                <img
                  src="/vaca.png"
                  alt="Vaca"
                  style={{
                    width: sizeConfig.iconSize * 0.8,
                    height: sizeConfig.iconSize * 0.8,
                    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
                  }}
                />
              ) : (
                <IconComponent
                  sx={{
                    fontSize: sizeConfig.iconSize * 0.65,
                    color: variantConfig.color,
                    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Mensagem de loading com efeito shimmer */}
          <Typography
            variant="body1"
            sx={{
              color: DESIGN_TOKENS.colors.text.primary,
              fontSize: sizeConfig.fontSize,
              fontWeight: 600,
              maxWidth: 320,
              lineHeight: 1.5,
              mb: 2,
              background: `linear-gradient(
                90deg,
                ${DESIGN_TOKENS.colors.text.primary} 0%,
                ${variantConfig.color} 50%,
                ${DESIGN_TOKENS.colors.text.primary} 100%
              )`,
              backgroundSize: '200% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: `${shimmerAnimation} 2s ease-in-out infinite`,
            }}
          >
            {variantConfig.message}
          </Typography>

          {/* Indicador de pontos animados melhorado */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${variantConfig.color} 0%, ${variantConfig.color}80 100%)`,
                  boxShadow: `0 0 10px ${variantConfig.color}40`,
                  animation: `${pulseAnimation} 1.8s ease-in-out infinite`,
                  animationDelay: `${index * 0.3}s`,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default AgricultureLoading;